import {
  Users,
  Phone,
  MessageCircle,
  Clock,
  CreditCard,
  Calendar,
  CheckCircle,
  FileText,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Briefcase,
} from 'lucide-react'

export interface LoanOfficer {
  id: string
  name: string
  avatar: string
  activity: {
    dials: number
    conversations: number
    talkTime: string // HH:MM
    creditPulls: number
    appointments: number
    followUps: number
    docsCollected: number
  }
  conversion: {
    dialToConv: number // %
    convToPull: number // %
    pullToApp: number // %
    appToSubmit: number // %
    submitToLock: number // %
    lockToFund: number // %
    pullToFund: number // % (Total Effectiveness)
  }
  pipeline: {
    activeApps: number
    submittedNotLockedCount: number
    submittedNotLockedVol: number
    lockedNotFundedCount: number
    lockedNotFundedVol: number
    avgPipelineSize: number
    aging: {
      days0_7: number
      days8_14: number
      days15_30: number
      days30plus: number
    }
    coverageRatio: number // Goal is 3x
  }
  results: {
    locksMTD: number
    fundedUnits: number
    fundedVolume: number
    revenue: number
    revenuePerLO: number
    revenuePerFundedFile: number
  }
}

export const loanOfficers: LoanOfficer[] = [
  {
    id: '1',
    name: 'Sarah Chen',
    avatar: 'SC',
    activity: {
      dials: 2400,
      conversations: 840,
      talkTime: '92:30',
      creditPulls: 180,
      appointments: 45,
      followUps: 520,
      docsCollected: 38,
    },
    conversion: {
      dialToConv: 35,
      convToPull: 21,
      pullToApp: 75,
      appToSubmit: 88,
      submitToLock: 82,
      lockToFund: 94,
      pullToFund: 48,
    },
    pipeline: {
      activeApps: 22,
      submittedNotLockedCount: 8,
      submittedNotLockedVol: 1800000,
      lockedNotFundedCount: 12,
      lockedNotFundedVol: 3200000,
      avgPipelineSize: 2500000,
      aging: { days0_7: 10, days8_14: 6, days15_30: 4, days30plus: 2 },
      coverageRatio: 4.5,
    },
    results: {
      locksMTD: 12,
      fundedUnits: 9,
      fundedVolume: 2450000,
      revenue: 68000,
      revenuePerLO: 68000,
      revenuePerFundedFile: 7555,
    },
  },
  {
    id: '2',
    name: 'Marcus Johnson',
    avatar: 'MJ',
    activity: {
      dials: 1900,
      conversations: 570,
      talkTime: '75:15',
      creditPulls: 140,
      appointments: 32,
      followUps: 410,
      docsCollected: 25,
    },
    conversion: {
      dialToConv: 30,
      convToPull: 24,
      pullToApp: 68,
      appToSubmit: 82,
      submitToLock: 78,
      lockToFund: 90,
      pullToFund: 35,
    },
    pipeline: {
      activeApps: 15,
      submittedNotLockedCount: 5,
      submittedNotLockedVol: 1200000,
      lockedNotFundedCount: 8,
      lockedNotFundedVol: 2100000,
      avgPipelineSize: 1800000,
      aging: { days0_7: 6, days8_14: 5, days15_30: 3, days30plus: 1 },
      coverageRatio: 3.2,
    },
    results: {
      locksMTD: 8,
      fundedUnits: 6,
      fundedVolume: 1650000,
      revenue: 42000,
      revenuePerLO: 42000,
      revenuePerFundedFile: 7000,
    },
  },
  {
    id: '3',
    name: 'Emily Rodriguez',
    avatar: 'ER',
    activity: {
      dials: 2100,
      conversations: 735,
      talkTime: '88:00',
      creditPulls: 165,
      appointments: 40,
      followUps: 480,
      docsCollected: 32,
    },
    conversion: {
      dialToConv: 35,
      convToPull: 22,
      pullToApp: 72,
      appToSubmit: 85,
      submitToLock: 80,
      lockToFund: 92,
      pullToFund: 42,
    },
    pipeline: {
      activeApps: 19,
      submittedNotLockedCount: 7,
      submittedNotLockedVol: 1600000,
      lockedNotFundedCount: 10,
      lockedNotFundedVol: 2800000,
      avgPipelineSize: 2200000,
      aging: { days0_7: 8, days8_14: 6, days15_30: 3, days30plus: 2 },
      coverageRatio: 3.8,
    },
    results: {
      locksMTD: 10,
      fundedUnits: 8,
      fundedVolume: 2100000,
      revenue: 55000,
      revenuePerLO: 55000,
      revenuePerFundedFile: 6875,
    },
  },
  {
    id: '4',
    name: 'David Kim',
    avatar: 'DK',
    activity: {
      dials: 1500,
      conversations: 375,
      talkTime: '55:45',
      creditPulls: 90,
      appointments: 20,
      followUps: 300,
      docsCollected: 15,
    },
    conversion: {
      dialToConv: 25,
      convToPull: 24,
      pullToApp: 60,
      appToSubmit: 75,
      submitToLock: 70,
      lockToFund: 85,
      pullToFund: 28,
    },
    pipeline: {
      activeApps: 10,
      submittedNotLockedCount: 3,
      submittedNotLockedVol: 700000,
      lockedNotFundedCount: 5,
      lockedNotFundedVol: 1200000,
      avgPipelineSize: 1000000,
      aging: { days0_7: 3, days8_14: 3, days15_30: 2, days30plus: 2 },
      coverageRatio: 2.1, // Below target
    },
    results: {
      locksMTD: 5,
      fundedUnits: 4,
      fundedVolume: 950000,
      revenue: 24000,
      revenuePerLO: 24000,
      revenuePerFundedFile: 6000,
    },
  },
  {
    id: '5',
    name: 'Jessica Park',
    avatar: 'JP',
    activity: {
      dials: 2600,
      conversations: 910,
      talkTime: '98:20',
      creditPulls: 200,
      appointments: 50,
      followUps: 550,
      docsCollected: 42,
    },
    conversion: {
      dialToConv: 35,
      convToPull: 22,
      pullToApp: 78,
      appToSubmit: 90,
      submitToLock: 85,
      lockToFund: 95,
      pullToFund: 52,
    },
    pipeline: {
      activeApps: 25,
      submittedNotLockedCount: 9,
      submittedNotLockedVol: 2000000,
      lockedNotFundedCount: 14,
      lockedNotFundedVol: 3600000,
      avgPipelineSize: 2800000,
      aging: { days0_7: 12, days8_14: 7, days15_30: 4, days30plus: 2 },
      coverageRatio: 5.1,
    },
    results: {
      locksMTD: 14,
      fundedUnits: 11,
      fundedVolume: 2900000,
      revenue: 78000,
      revenuePerLO: 78000,
      revenuePerFundedFile: 7090,
    },
  },
]

// Helper to calculate company totals
export const companyTotals = {
  activity: {
    dials: loanOfficers.reduce((acc, lo) => acc + lo.activity.dials, 0),
    conversations: loanOfficers.reduce(
      (acc, lo) => acc + lo.activity.conversations,
      0,
    ),
    creditPulls: loanOfficers.reduce(
      (acc, lo) => acc + lo.activity.creditPulls,
      0,
    ),
    appointments: loanOfficers.reduce(
      (acc, lo) => acc + lo.activity.appointments,
      0,
    ),
    followUps: loanOfficers.reduce((acc, lo) => acc + lo.activity.followUps, 0),
    docsCollected: loanOfficers.reduce(
      (acc, lo) => acc + lo.activity.docsCollected,
      0,
    ),
    // Simple sum for talk time hours roughly
    talkTimeHours: 410,
  },
  pipeline: {
    activeApps: loanOfficers.reduce(
      (acc, lo) => acc + lo.pipeline.activeApps,
      0,
    ),
    submittedVol: loanOfficers.reduce(
      (acc, lo) => acc + lo.pipeline.submittedNotLockedVol,
      0,
    ),
    lockedVol: loanOfficers.reduce(
      (acc, lo) => acc + lo.pipeline.lockedNotFundedVol,
      0,
    ),
    avgCoverage: 3.74,
  },
  results: {
    locksMTD: loanOfficers.reduce((acc, lo) => acc + lo.results.locksMTD, 0),
    fundedUnits: loanOfficers.reduce(
      (acc, lo) => acc + lo.results.fundedUnits,
      0,
    ),
    fundedVolume: loanOfficers.reduce(
      (acc, lo) => acc + lo.results.fundedVolume,
      0,
    ),
    revenue: loanOfficers.reduce((acc, lo) => acc + lo.results.revenue, 0),
  },
}
